<!--
This component has a rather unconventional design as a result of its
intended behavior and some technical limitations. This comment serves
to describe and explain some of its quirks.

At a high level, the options work by finding DOM elements matching
a given selector and dynamically adding or removing a class that
will hide the element. This breaks the Vue model and conventions,
but since the article is HTML without associated Vue components
for its child elements, this design is necessary without a lot
of additional glue.

A result of this design is the necessity to synchronize the
calls to add or remove the "hidden" class outside of Vue convention.
Using computed properties, for example, would necessitate adding
side effects to the computation, which would be unpredictable.

Previously, these synchronizations were triggered on checkbox state
changes. However, whether or not a section is hidden is not strictly
dependent on its own checkbox state, making this synchronization
non-trivial, and manually performing this would be error prone.

The setSectionProp and getSectionProp helper methods were designed
as an abstraction around these challenges, allowing some level of
automatic management of class addition/removal alongside writing
property dependencies that would traditionally be accomplished
with computed methods. Please use these methods for accessing the
properties on section objects.
-->

<template>
  <div v-if="isPrintView"
       id="printControls"
       class="utd-print"
       :class="printControlClasses">
    <div class="print-controls-title">
      <h5>{{ $t('CONTENT.PRINT_OPTIONS') }}</h5>
    </div>

    <div v-if="hasAlternateLanguages" class="language-select">
      <utd-select v-if="currentLanguage !== null"
                  id="selectLanguage"
                  v-model="currentLanguage"
                  small
                  name="selectLanguage"
                  :use-index="false"
                  class="wk-js2"
                  placeholder="Select a Language"
                  :options="sortedAltLanguages"
                  value-prop="id"
                  label-prop="name"
                  @utd-select="changeLanguage()" />
    </div>
    <div v-if="isTopic">
      <div v-for="section in visibleSections"
           :key="getSectionProp(section, 'ctrlId')"
           class="row"
           :class="getSectionProp(section, 'rowClass')">
        <utd-checkbox :id="getSectionProp(section, 'ctrlId')"
                      class="value"
                      :class="{'is-disabled': getSectionProp(section, 'disabled')}"
                      :checked="getSectionProp(section, 'isChecked')"
                      :disabled="getSectionProp(section, 'disabled')"
                      @checkbox-changed="(checked) => toggleSection(section, checked)">
          {{ getSectionProp(section, 'ignoreLocalization')
            ? getSectionProp(section, 'label') : $t(getSectionProp(section, 'label')) }}
        </utd-checkbox>
      </div>
    </div>
    <utd-button class="print-button"
                tabindex="0"
                button-icon-position="right"
                button-icon="arrow-right"
                @click="print()">
      {{ $t('CONTENT.TOOLS_PRINT') }}
    </utd-button>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { getWindow } from '_acaSrc/utility/DOM';
import utdBack from '_acaSrc/directives/UtdBack.directive';
import UtdCheckbox from '_acaSrc/components/shared/stdlib/UtdCheckbox.vue';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdSelect from '_acaSrc/components/shared/input/UtdSelect.vue';

import {
    SET_GRAPHIC_URL_LANGUAGE
} from '_acaSrc/store/graphic.store';
import {
    SET_TOPIC_LANGUAGE
} from '_acaSrc/store/topic/topic.store';

const UI_RENDER_DELAY_MS = 100;
const HIDDEN_CLASS = 'print-section-hidden';
const TEXT_ELEMENT_ID = 'topicText';
export default {
    directives: {
        utdBack
    },
    components: {
        UtdCheckbox,
        UtdSelect,
        UtdButton
    },
    props: {
        alternateLanguages: {
            type: Array,
            default() {
                return [];
            }
        },
        type: String // Can be: graphic, topic
    },
    data() {
        return {
            currentLanguage: null,
            defaultSectionValues: {
                showSection: (section) =>
                    this.getSectionProp(section, 'isChecked') &&
                    !this.getSectionProp(section, 'disabled'),
                rowClass: 'indent-level-0',
                ignoreLocalization: false,
                disabled: false,
                hasSection: false,
                isChecked: true,
                elArr: null
            },
            sections: [{
                /* 
                    function values can be set to field to have them calculated
                    on access using given accessor methods

                    make sure to use this.setSectionProp and this.getSectionProp
                    methods for access and rendering to preserve this behavior,
                    as well as to utilize specified defaults above

                    hidden classes are automatically updated depending on the
                    value of showSection when props are set
                */

                ctrlId: 'inpText', // #id of checkbox control
                label: 'CONTENT.PRINT_TEXT', // This is the translation string
                domElSelector: '#topicText' // selector for section / class of elements
                /* showSection: // Flag controlling showing/hiding of section */
                /* isChecked: // Flag controlling checkbox state */
                /* elArr: // Stores actual references to section object in DOM */
                /* hasSection: // If true, will render control row */
                /* rowClass: // set classes on div for row */
                // can add more .indent-level-[#] style blocks below to add indent options
                /* ignoreLocalization: // if true, use string as is */
                /* disabled: // if true, disables button and hides section */
            }, {
                ctrlId: 'inpRefs',
                label: 'CONTENT.PRINT_REFERENCES',
                domElSelector: '#references'
            }, {
                ctrlId: 'inpGfxs',
                label: 'CONTENT.PRINT_GRAPHICS',
                domElSelector: '#printGraphics'
            }, {
                ctrlId: 'inpInlineGfxs',
                label: 'CONTENT.PRINT_INLINE_GRAPHICS',
                domElSelector: '.utd-inline-graphics',
                rowClass: 'indent-level-1',
                disabled: (section, sections) => {
                    const text = sections.find(x => this.getSectionProp(x, 'ctrlId') === 'inpText');
                    const gfxs = sections.find(x => this.getSectionProp(x, 'ctrlId') === 'inpGfxs');
                    const textIsChecked = this.getSectionProp(text, 'isChecked');
                    const gfxsIsChecked = this.getSectionProp(gfxs, 'isChecked');
                    return !(textIsChecked && gfxsIsChecked);
                }
            }, {
                ctrlId: 'inpDisc',
                label: 'CONTENT.PRINT_DISCLOSURES',
                domElSelector: '#printDisclosures'
            }]
        };
    },
    computed: {
        ...mapGetters('app', [
            'router',
            'isProspectMode',
            'isPrintView'
        ]),
        ...mapGetters('topic', [ 'topicLanguage' ]),
        ...mapGetters('graphic', [
            'graphicLanguage'
        ]),
        hasAlternateLanguages() {
            return this.alternateLanguages.length > 1;
        },
        isTopic() {
            return this.type === 'topic';
        },
        isGraphic() {
            return this.type === 'graphic';
        },
        language() {
            if (this.isTopic) {
                return this.topicLanguage;
            }
            return this.graphicLanguage;
        },
        visibleSections() {
            return this.sections.filter(x => this.getSectionProp(x, 'hasSection'));
        },
        isOnlyPrint() {
            if (!this.isTopic) {
                return true;
            }
            return (this.visibleSections.length === 1
                   && this.getSectionProp(this.visibleSections[0], 'domElSelector')
                    === `#${TEXT_ELEMENT_ID}`);
        },
        printControlClasses() {
            const printOnlyClass = this.isOnlyPrint && !this.hasAlternateLanguages
                ? 'is-print-only' : '';
            const hideSectionClass = this.isOnlyPrint && 'hide-sections' || '';
            return [
                this.currentLanguage,
                hideSectionClass,
                printOnlyClass
            ];
        },
        sortedAltLanguages() {
            const langs = [ ...this.alternateLanguages ];
            langs.sort();
            return langs;
        }
    },
    mounted() {
        setTimeout(() => {
            this.activate();
        }, UI_RENDER_DELAY_MS);
    },
    methods: {
        ...mapMutations('topic', [
            SET_TOPIC_LANGUAGE
        ]),
        ...mapMutations('graphic', [
            SET_GRAPHIC_URL_LANGUAGE
        ]),
        ...mapActions('topic', [
            'hideOrShowPathways',
            'showRxTransitionsLinks',
            'logTopicPrintEvent'
        ]),
        print() {
            getWindow().print();
            this.isTopic && this.logTopicPrintEvent({ requestedView: 'print' });
        },
        // safely sets prop on section without overwriting existing function
        // use this method instead of direct mutation
        setSectionProp(section, key, value) {
            if (section && !(typeof section[key] === 'function')) {
                section[key] = value;
            }
            this.updateHiddenClasses();
        },
        // returns value, or computes function value
        getSectionProp(section, key) {
            const explicitVal = section?.[key];
            const value = explicitVal === undefined ? this.defaultSectionValues[key] : explicitVal;
            return typeof value === 'function' ? value(section, this.sections) : value;
        },
        updateHiddenClasses() {
            for (const section of this.sections) {
                const elArr = this.getSectionProp(section, 'elArr');
                const showSection = this.getSectionProp(section, 'showSection');
                if (elArr) {
                    for (const el of elArr) {
                        if (el && el.classList) {
                            showSection
                                ? el.classList.remove(HIDDEN_CLASS)
                                : el.classList.add(HIDDEN_CLASS);
                        }
                    }
                }
            }
        },
        updatePrintUrlLang() {
            // Adds language to URL path so language is maintained on print page refresh
            // back/forward (we don't show english in url); DE10176 kevin f
            const printPath = this.router.url();
            let printPathLang = '';
            for (const lang in this.alternateLanguages) {
                if (printPath.indexOf(`/${this.alternateLanguages[lang].id}/`) > -1) {
                    if (this.currentLanguage === 'en-US') {
                        printPathLang
                        = printPath.replace(`${this.alternateLanguages[lang].id}/`, '');
                        break;
                    }
                    printPathLang = printPath.replace(`/${this.alternateLanguages[lang].id}/`,
                        `/${this.currentLanguage}/`);
                    break;
                }
            }
            if (printPathLang === '' && this.currentLanguage !== 'en-US') {
                printPathLang = this.updatePrintPathUrlLang(printPath);
            }
            getWindow().location.href = printPathLang;
        },
        updatePrintPathUrlLang(printPath) {
            let printPathLang = '';
            const pageLang = printPath.match(new RegExp('contents/(.*)/image'));
            if (pageLang !== null) {
                printPathLang = printPath.replace(pageLang[1], `${this.currentLanguage}`);
            }
            else {
                printPathLang
                = printPath.replace('contents', `contents/${this.currentLanguage}`);
            }
            return printPathLang;
        },
        changeLanguage() {
            if (this.isTopic) {
                this.changeTopicLang();
            }
            else if (this.isGraphic) {
                this.changeGraphicLang();
            }
        },
        changeTopicLang() {
            this[SET_TOPIC_LANGUAGE](this.currentLanguage);
            this.updatePrintUrlLang();
        },
        changeGraphicLang() {
            this[SET_GRAPHIC_URL_LANGUAGE](this.currentLanguage);
            this.updatePrintUrlLang();
        },
        toggleSection(section, shouldShow) {
            this.setSectionProp(section, 'isChecked', shouldShow);
        },
        activate() {
            // If we have alternate languages, initialize select control's value
            if (this.hasAlternateLanguages) {
                this.alternateLanguages.some(lang => {
                    if (lang.id === this.language) {
                        this.currentLanguage = lang.id;
                        return true;
                    }
                    return false;
                });
            }

            if (!this.isTopic) {
                return;
            }

            this.sections.forEach(section => {
                // Attempt to get a handle to the section DOM element
                const domElSelector = this.getSectionProp(section, 'domElSelector');
                this.setSectionProp(section, 'elArr', document.querySelectorAll(domElSelector));
                // Indicate whether the section was located
                const elArr = this.getSectionProp(section, 'elArr');
                this.setSectionProp(section, 'hasSection', elArr.length > 0);

                const ctrlId = this.getSectionProp(section, 'ctrlId');
                // If 'articleOnly' argument passed, hide any other sections on load
                if (ctrlId !== 'inpText' && this.router.getParams('articleOnly')) {
                    this.toggleSection(section, false);
                }
                // If prospect view, disable graphic print.
                if (ctrlId === 'inpGfxs' && this.isProspectMode) {
                    this.toggleSection(section, false);
                    this.setSectionProp(section, 'hasSection', false);
                }
            });

            this.hideOrShowPathways();
            this.showRxTransitionsLinks();
        },
        isBackButtonDisabled() {
            return getWindow().history.length < 2;
        }
    }
};
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.topicPrint {
  * {
    box-sizing: border-box;
  }

  .print-controls-title {
    padding: 12px 18px;
    background-color: @DS1-UTD-PRINT-OPTIONS-BG-COLOR;
    border-top: @DS1-UTD-PRIMARY-CARD-BORDER;
    border-bottom: @DS1-UTD-GRAY-SINGLE-BORDER;

    h5 {
      .ds1-utd-h5();
      .ds1-ma-0();
    }
  }
}

.utd-print {
  display: block;
  position: fixed;
  top: 16px;
  max-width: 168px;
  right: 24px;
  width: 100%;
  padding-bottom: 42px;
  border: @DS1-UTD-GRAY-SINGLE-BORDER;
  border-bottom-width: 0;
  background-color: @DS1-UTD-PRIMARY-BG-COLOR;
  box-shadow: @DS1-BOX-SHADOW-PRINT-TOOLS;
  z-index: 20;

  &.ar {
    right: auto;
    left: 24px;
  }

  .print-button {
    .ds1-ph-2();
    position: absolute;
    bottom: 0;
    right: 0;
    display: block;
    width: 100%;
    height: 42px;
    font-size: 14px;
    text-align: right;
    font-weight: bold;
  }

  .row {
    .ds1-pt-compact();
    display: flex;
    align-items: center;
    margin: 6px 0;
    padding-right: 20px;

    &:first-child {
      margin-top: 14px;
    }

    &:last-child {
      .ds1-mb-2();
    }

    &.indent-level-0 {
        padding-left: 20px;
    }

    &.indent-level-1 {
        padding-left: 30px;
    }
  }

  .language-select {
    padding: 16px 20px 0;

    .wk-field-select {
      height: 32px;
    }

    .wk-field-select-container::after {
      top: 2px;
    }
  }

  &.is-print-only {
    max-width: 98px;
    box-shadow: none;
    border-width: 0;
    padding: 0;

    .print-controls-title,
    .row {
      display: none;
    }

    .print-button {
      position: static;
      text-align: center;
    }
  }

  &.hide-sections {
    padding-bottom: 60px;

    .row {
      display: none;
    }
  }
}
</style>

<style lang="less">
#printControls {
    .wkce-checkbox {
        display: flex;
        align-items: center;

        .wkce-checkbox__label-text {
            font-size: 14px;
            line-height: 18px;
        }
    }
}
</style>